// Import Dependencies
import { Navigate, useLocation, useOutlet } from "react-router";

// Local Imports
import { useAuthContext } from "app/contexts/auth/context";
import { GHOST_ENTRY_PATH } from "../constants/app.constant";
import { navigation } from "app/navigation";
import { hasAllPermissions } from "utils/permissionsUtils";

// ----------------------------------------------------------------------

// Recursively build a flat map of path -> required permissions array (including parent permissions)
const buildPermissionMap = (items, parentPermissions = []) => {
  const map = {};
  for (const item of items) {
    const currentPermissions = [...parentPermissions];
    if (item.permissionCode) {
      currentPermissions.push(item.permissionCode);
    }
    if (item.path) {
      map[item.path] = currentPermissions;
    }
    if (item.childs && Array.isArray(item.childs)) {
      Object.assign(map, buildPermissionMap(item.childs, currentPermissions));
    }
  }
  return map;
};

const permissionMap = buildPermissionMap(navigation);

// Find the longest matching prefix for the current pathname
const findRequiredPermissions = (pathname, permissionMap) => {
  const cleanPath = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
  const sortedPaths = Object.keys(permissionMap).sort((a, b) => b.length - a.length);

  for (const path of sortedPaths) {
    if (cleanPath === path || cleanPath.startsWith(path + "/")) {
      return permissionMap[path];
    }
  }
  return [];
};

export default function AuthGuard() {
  const outlet = useOutlet();
  const { isAuthenticated } = useAuthContext();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`${GHOST_ENTRY_PATH}`}
        replace
      />
    );
  }

  // Check if user has required permissions for the active path
  const requiredPermissions = findRequiredPermissions(location.pathname, permissionMap);
  if (requiredPermissions.length > 0 && !hasAllPermissions(requiredPermissions)) {
    return (
      <Navigate
        to="/no-access"
        replace
      />
    );
  }

  return <>{outlet}</>;
}

